import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Landing from "./pages/Landing";
import Search from "./pages/Search";
import ListingDetail from "./pages/ListingDetail";
import CreateListing from "./pages/CreateListing";
import Dashboard from "./pages/Dashboard";
import Resources from "./pages/Resources";
import AuthCallback from "./pages/AuthCallback";
import MediaKit from "./pages/MediaKit";
import RoleSelection from "./pages/RoleSelection";
import Upgrade from "./pages/Upgrade";
import Terms from "./pages/Terms";
import HomeValuation from "./pages/HomeValuation";
import ImportListing from "./pages/ImportListing";
import FBLanding from "./pages/FBLanding";
import SellerResources from "./pages/SellerResources";
import BuyerResources from "./pages/BuyerResources";
import ProfessionalServices from "./pages/ProfessionalServicesNew";
import AffiliateROI from "./pages/AffiliateROI";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TwoFactorAuth from "./pages/TwoFactorAuth";
import AdminDashboard from "./pages/AdminDashboard";
import AdminSettings from "./pages/AdminSettings";
import CityLanding from "./pages/CityLandingEnhanced";
import CountyLanding from "./pages/CountyLanding";
import RealtorApplication from "./pages/RealtorApplication";
import RealtorDashboard from "./pages/RealtorDashboard";
import RequestAgent from "./pages/RequestAgent";
import BlogList from "./pages/BlogList";
import BlogDetail from "./pages/BlogDetail";
import PressReleaseList from "./pages/PressReleaseList";
import PressReleaseDetail from "./pages/PressReleaseDetail";
import VideoGallery from "./pages/VideoGallery";
import SEOTools from "./pages/SEOTools";
import MarketplaceTools from "./pages/MarketplaceTools";
import OurHistory from "./pages/OurHistory";
import FranchiseInquiry from "./pages/FranchiseInquiry";
import SMSTerms from "./pages/SMSTerms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
// Contest pages disabled until site has traffic - re-enable later
// import ContestEntry from "./pages/ContestEntry";
// import Leaderboard from "./pages/Leaderboard";
import Footer from "./components/Footer";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor to include credentials
axios.defaults.withCredentials = true;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading Its-Sold.com...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <div className="flex-grow">
          <Routes>
            <Route path="/" element={<RoleSelection />} />
            <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login setUser={setUser} />} />
            <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <Register setUser={setUser} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/seller" element={<Landing user={user} role="seller" />} />
            <Route path="/buyer" element={<Landing user={user} role="buyer" />} />
            <Route path="/search" element={user ? <Search user={user} /> : <Navigate to="/register?role=buyer" />} />
            <Route path="/listing/:id" element={<ListingDetail user={user} />} />
            <Route path="/create" element={user ? <CreateListing user={user} /> : <Navigate to="/" />} />
            <Route path="/dashboard" element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/" />} />
            <Route path="/2fa" element={user ? <TwoFactorAuth /> : <Navigate to="/login" />} />
            <Route path="/admin" element={user ? <AdminDashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} />
            <Route path="/realtor-application" element={<RealtorApplication />} />
            <Route path="/realtor-dashboard" element={user ? <RealtorDashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} />
            <Route path="/request-agent" element={<RequestAgent />} />
            <Route path="/admin/settings" element={user ? <AdminSettings /> : <Navigate to="/login" />} />
            <Route path="/media-kit/:id" element={user ? <MediaKit user={user} /> : <Navigate to="/" />} />
            <Route path="/upgrade/:id" element={user ? <Upgrade user={user} /> : <Navigate to="/" />} />
            <Route path="/resources" element={<Resources user={user} />} />
            <Route path="/seller-resources" element={<SellerResources user={user} />} />
            <Route path="/buyer-resources" element={<BuyerResources user={user} />} />
            <Route path="/home-valuation" element={<HomeValuation user={user} />} />
            <Route path="/professional-services" element={<ProfessionalServices user={user} />} />
            <Route path="/affiliate-roi" element={<AffiliateROI />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/import" element={user ? <ImportListing user={user} /> : <Navigate to="/fb-landing" />} />
            <Route path="/fb-landing" element={<FBLanding />} />
            <Route path="/auth/callback" element={<AuthCallback setUser={setUser} />} />
            <Route path="/homes-for-sale/:citySlug" element={<CityLanding user={user} />} />
            <Route path="/county/:countySlug" element={<CountyLanding user={user} />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/press-releases" element={<PressReleaseList />} />
            <Route path="/press-releases/:id" element={<PressReleaseDetail />} />
            <Route path="/videos" element={<VideoGallery />} />
            <Route path="/admin/seo-tools" element={user?.is_super_admin || user?.is_admin ? <SEOTools /> : <Navigate to="/" />} />
            <Route path="/marketplace-tools" element={user?.is_admin ? <MarketplaceTools user={user} /> : <Navigate to="/" />} />
            <Route path="/history" element={<OurHistory />} />
            <Route path="/our-story" element={<OurHistory />} />
            <Route path="/franchise-opportunities" element={<FranchiseInquiry />} />
            <Route path="/sms-terms" element={<SMSTerms />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            {/* Contest routes disabled until site has traffic - re-enable later
            <Route path="/contest" element={<ContestEntry />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            */}
          </Routes>
        </div>
        <Footer />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;